// components/resources/ResourceRegistration.jsx
import React, { useState } from 'react';
import { X, Upload, Calendar, Info, Check, Truck, Wrench, Building } from 'lucide-react';
import { useResources } from '../../context/ResourceContext';
import { useTrust } from '../../context/TrustContext';
import Modal from '../common/Modal';

const ResourceRegistration = ({ isOpen, onClose, onSuccess, category = null }) => {
  const { resourceCategories, registerResource } = useResources();
  const { userTrust } = useTrust();
  
  const [selectedCategory, setSelectedCategory] = useState(category || 'vehicles');
  const [formData, setFormData] = useState({
    type: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    capacity: '',
    capacityUnit: '',
    features: [],
    condition: 'excellent',
    description: '',
    photos: [],
    documents: [],
    location: '',
    hasInsurance: false,
    availability: {
      anytime: false,
      weekends: true,
      evenings: false,
      schedule: []
    }
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const categoryIcons = {
    vehicles: Truck,
    equipment: Wrench,
    spaces: Building
  };

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setFormData(prev => ({
      ...prev,
      type: '',
      capacityUnit: ''
    }));
    setErrors({});
  };

  const handleTypeChange = (typeId) => {
    const type = resourceCategories[selectedCategory].types.find(t => t.id === typeId);
    setFormData(prev => ({
      ...prev,
      type: typeId,
      capacityUnit: type?.capacityUnit || ''
    }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.type) {
      newErrors.type = 'Please select a resource type';
    }
    
    if (selectedCategory === 'vehicles') {
      if (!formData.make) newErrors.make = 'Vehicle make is required';
      if (!formData.model) newErrors.model = 'Vehicle model is required';
    }
    
    if (!formData.capacity || formData.capacity <= 0) {
      newErrors.capacity = 'Capacity is required';
    }
    
    if (!formData.description) {
      newErrors.description = 'Description is required';
    }
    
    if (userTrust?.score < 3.5) {
      newErrors.trust = 'You need a trust score of 3.5 or higher to register resources';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const resource = await registerResource(selectedCategory, formData);
      onSuccess(resource);
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getFeatureOptions = () => {
    switch (selectedCategory) {
      case 'vehicles':
        return ['GPS tracking', 'Lift gate', 'Climate control', 'Tie-down straps', 'Moving blankets'];
      case 'equipment':
        return ['Well maintained', 'Recently serviced', 'Safety certified', 'Includes manual'];
      case 'spaces':
        return ['24/7 access', 'Security system', 'Loading dock', 'Climate controlled', 'Forklift available'];
      default:
        return [];
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Register Resource"
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Trust Score Check */}
        {userTrust?.score < 3.5 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="text-yellow-600 mt-0.5" size={16} />
              <div>
                <p className="text-sm font-medium text-yellow-800">Trust Score Required</p>
                <p className="text-sm text-yellow-700 mt-1">
                  You need a trust score of 3.5 or higher to register resources. 
                  Your current score is {userTrust?.score?.toFixed(1)}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Resource Category
          </label>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(resourceCategories).map(([key, cat]) => {
              const Icon = categoryIcons[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleCategoryChange(key)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedCategory === key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`mx-auto mb-2 ${
                    selectedCategory === key ? 'text-blue-600' : 'text-gray-400'
                  }`} size={24} />
                  <div className={`text-sm font-medium ${
                    selectedCategory === key ? 'text-blue-900' : 'text-gray-700'
                  }`}>
                    {cat.name}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Resource Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Resource Type *
          </label>
          <select
            value={formData.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
              errors.type ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Select a type</option>
            {resourceCategories[selectedCategory].types.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.type && (
            <p className="text-sm text-red-600 mt-1">{errors.type}</p>
          )}
        </div>

        {/* Vehicle-specific fields */}
        {selectedCategory === 'vehicles' && (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Make *
              </label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.make ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Ford"
              />
              {errors.make && (
                <p className="text-xs text-red-600 mt-1">{errors.make}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model *
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.model ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., F-150"
              />
              {errors.model && (
                <p className="text-xs text-red-600 mt-1">{errors.model}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="1990"
                max={new Date().getFullYear() + 1}
              />
            </div>
          </div>
        )}

        {/* Capacity */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Capacity *
            </label>
            <input
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.capacity ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter capacity"
              min="0"
            />
            {errors.capacity && (
              <p className="text-xs text-red-600 mt-1">{errors.capacity}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit
            </label>
            <input
              type="text"
              value={formData.capacityUnit}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
            />
          </div>
        </div>

        {/* Condition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Condition
          </label>
          <div className="grid grid-cols-4 gap-2">
            {['excellent', 'good', 'fair', 'needs work'].map(condition => (
              <label key={condition} className="flex items-center">
                <input
                  type="radio"
                  name="condition"
                  value={condition}
                  checked={formData.condition === condition}
                  onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                  className="mr-2"
                />
                <span className="text-sm capitalize">{condition}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Insurance */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Insurance
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.hasInsurance}
              onChange={(e) => setFormData(prev => ({ ...prev, hasInsurance: e.target.checked }))}
              className="mr-2 rounded"
            />
            <span className="text-sm">This resource is insured</span>
          </label>
        </div>

        {/* Features */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Features & Amenities
          </label>
          <div className="space-y-2">
            {getFeatureOptions().map(feature => (
              <label key={feature} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.features.includes(feature)}
                  onChange={() => handleFeatureToggle(feature)}
                  className="mr-2 rounded"
                />
                <span className="text-sm">{feature}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className={`w-full px-3 py-2 border rounded-lg ${
              errors.description ? 'border-red-300' : 'border-gray-300'
            }`}
            rows={3}
            placeholder="Describe your resource, including any special features or limitations..."
          />
          {errors.description && (
            <p className="text-sm text-red-600 mt-1">{errors.description}</p>
          )}
        </div>

        {/* Availability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            General Availability
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.availability.anytime}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  availability: { ...prev.availability, anytime: e.target.checked }
                }))}
                className="mr-2 rounded"
              />
              <span className="text-sm">Available anytime</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.availability.weekends}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  availability: { ...prev.availability, weekends: e.target.checked }
                }))}
                className="mr-2 rounded"
              />
              <span className="text-sm">Available on weekends</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.availability.evenings}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  availability: { ...prev.availability, evenings: e.target.checked }
                }))}
                className="mr-2 rounded"
              />
              <span className="text-sm">Available evenings (after 5 PM)</span>
            </label>
          </div>
        </div>

        {/* Photo Upload (placeholder) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photos (Optional)
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="mx-auto text-gray-400 mb-2" size={32} />
            <p className="text-sm text-gray-600">
              Upload photos of your resource
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Feature coming soon
            </p>
          </div>
        </div>

        {/* Error Display */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || userTrust?.score < 3.5}
            className={`flex-1 px-4 py-2 rounded-lg font-medium ${
              loading || userTrust?.score < 3.5
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? 'Registering...' : 'Register Resource'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ResourceRegistration;